import { logger } from '@adornis/base/logging.js';
import type { ValueEvent } from '@adornis/base/utilTypes.js';
import type { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { MultipleValidationError } from '@adornis/validation/errors/MultipleValidationError.js';
import '@chancenwerk/components/chancen-component-card-title.js';
import {
  LogistikIndividualOrderItem,
  LogistikOrderClearanceAlternative,
  LogistikOrderClearanceState,
  type LogistikOrder,
} from '@chancenwerk/db/LogistikOrder.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { firstValueFrom, timer } from 'rxjs';

export const acceptIndividualOrder = (order: LogistikOrder) =>
  ChromiumDialog.showPopup<boolean>({
    modal: true,
    content: (resolve, reject, el) => {
      const instance = el as ChemistryLitElement;
      let note = '';
      return html`
        <cr-flex w100 h100 space="lg" padding="md">
          <x-flex horizontal crossaxis-center space="md">
            <cr-icon ${css({ fontSize: instance.spacing.md, color: instance.colors.tone.success })}>
              check_circle
            </cr-icon>
            <cr-h3> Bestellung freigeben </cr-h3>
          </x-flex>
          <cr-text tone="subtle"> Sie können hier optional einen Kommentar zur Freigabe eintragen </cr-text>
          <logistik-component-tinymce
            @value-picked=${(e: ValueEvent<string>) => (note = e.detail.value)}
          ></logistik-component-tinymce>
          <x-flex horizontal end space="sm">
            <cr-button mode="outline" @click=${() => resolve(false)}> Abbrechen </cr-button>
            <cr-button
              @click=${async () => {
                try {
                  order.clearanceState = LogistikOrderClearanceState.APPROVED;
                  order.clearanceNote = note;
                  await ChromiumDialog.waitFor(Promise.all([firstValueFrom(timer(1000)), order.save()]));
                  resolve(true);
                  void ChromiumSnackbar.show('Bestellung erfolgreich freigegeben!');
                } catch (e) {
                  logger.error({ error: e }, 'Es gab ein Problem beim Freigeben der individuellen Bestellung');
                  void ChromiumDialog.alert(
                    'Es gab ein Problem beim Freigeben der individuellen Bestellung. Bitte versuchen Sie es später erneut!',
                  );
                }
              }}
            >
              Freigeben
            </cr-button>
          </x-flex>
        </cr-flex>
      `;
    },
  });

export const declineIndividualOrder = (order: LogistikOrder) =>
  ChromiumDialog.showPopup<boolean>({
    modal: true,
    content: (resolve, reject, el) => {
      const instance = el as ChemistryLitElement;
      let note = '';
      let price: number;
      let link = '';

      let alternatives: {
        oldLink: string;
        price: number;
        link: string;
      }[] = order.items.map(e => ({ price: 0, link: '', oldLink: (e as LogistikIndividualOrderItem).link }));

      return html`
        <cr-flex w100 h100 space="lg" padding="md">
          <x-flex horizontal crossaxis-center space="md">
            <cr-icon ${css({ fontSize: instance.spacing.md, color: instance.colors.tone.error })}> block </cr-icon>
            <cr-h3> Bestellung ablehnen </cr-h3>
          </x-flex>
          <cr-text tone="subtle">
            Sie können hier optional eine Beschreibung zum Ablehnen und einen Alternativ-Link hinzufügen
          </cr-text>
          <x-flex space="md">
            <logistik-component-tinymce
              @value-picked=${(e: ValueEvent<string>) => (note = e.detail.value)}
            ></logistik-component-tinymce>
            <x-flex space="md">
              ${repeat(
                alternatives,
                e => e.oldLink,
                (e, i) => {
                  const item = order.items[i] as LogistikIndividualOrderItem;
                  if (!item) {
                    return nothing;
                  }

                  return html`<cr-card padding="md" space="md">
                    <chancen-component-card-title>Produkt #${i + 1}</chancen-component-card-title>
                    <cr-flex horizontal crossaxis-center space="xs">
                      <cr-text bold> Link: </cr-text>
                      <cr-link href="${item.link}"><cr-text> Produkt </cr-text></cr-link>
                    </cr-flex>
                    <cr-input
                      placeholder="Link zu Alternative"
                      @value-picked=${(e: ValueEvent<string>) => (alternatives[i]!.link = e.detail.value)}
                    ></cr-input>
                    <cr-input
                      placeholder="Preis der Alternative"
                      postfix="&euro;"
                      type="number"
                      @value-picked=${(e: ValueEvent<number>) => (alternatives[i]!.price = e.detail.value)}
                    ></cr-input>
                    ${!item.acceptAlternative
                      ? html` <cr-text tone="warning">
                          Der Käufer hat angegeben, dass er keine alternativen Produkte möchte!
                        </cr-text>`
                      : nothing}
                  </cr-card>`;
                },
              )}
            </x-flex>
          </x-flex>
          <x-flex horizontal end space="sm">
            <cr-button mode="outline" @click=${() => resolve(false)}> Abbrechen </cr-button>
            <cr-button
              @click=${async () => {
                try {
                  order.clearanceState = LogistikOrderClearanceState.DECLINED;
                  order.clearanceNote = note;
                  order.clearanceAlternatives = alternatives
                    .filter(e => e.link)
                    .map(
                      e =>
                        new LogistikOrderClearanceAlternative({
                          oldLink: e.oldLink,
                          link: e.link,
                          price: e.price,
                        }),
                    );
                  await ChromiumDialog.waitFor(Promise.all([firstValueFrom(timer(1000)), order.save()]));
                  resolve(true);
                  return void ChromiumSnackbar.show('Bestellung abgelehnt!');
                } catch (e) {
                  logger.error(
                    { error: e, multiple: e instanceof MultipleValidationError },
                    'Es gab ein Problem beim Ablehnen der individuellen Bestellung',
                  );
                  ChromiumDialog.alert(
                    e instanceof MultipleValidationError &&
                      e.validationErrors[0] &&
                      ['clearanceNote', 'clearanceAlternatives'].includes(e.validationErrors[0].extensions.key)
                      ? e.validationErrors[0].message
                      : 'Es gab ein Problem beim Ablehnen der individuellen Bestellung. Bitte versuchen Sie es später erneut!',
                  );
                }
              }}
            >
              Ablehnen
            </cr-button>
          </x-flex>
        </cr-flex>
      `;
    },
  });

export const moveOrderBackToReview = async (order: LogistikOrder) => {
  if (
    !(await ChromiumDialog.confirm(
      'Möchten Sie diese abgelehnte Bestellung wirklich zurück in den Freigabeprozess schieben?',
    ))
  )
    return;

  try {
    order.clearanceState = LogistikOrderClearanceState.NEEDS_CLEARANCE;
    await ChromiumDialog.waitFor(order.save(), 'Bestellung wird verschoben...');
  } catch (err) {
    logger.error({ err }, 'Es gab ein Problem beim zurück Schiebe der Bestellung in den Freigabeprozess');
    return ChromiumDialog.alert(
      'Es gab ein Problem beim zurück Schieben der Bestellung in den Freigabeprozess. Bitte versuchen Sie es später erneut',
    );
  }
};
